class BundleBuilder extends HTMLElement {
    constructor() {
        super();
        this.bundleData = JSON.parse(this.querySelector('#bundle-data').innerHTML).sort((a, b) => a.count - b.count);
        this.selectedBundle = this.bundleData[0];
        this.tierFloor = this.bundleData[0].count;
        this.currencySymbol = this.getAttribute('data-currency-symbol');

        this.selectedProducts = [];

        this.initElements();

        this._ensureTierStyles();
        this.initGiftLadder();
        this.loadStateFromStorage();
        this.initializeTabStickyListener();
        this.initializeStickyButtonListener();
        this.initializeTabListeners();
        this.updateUI();
        this.initProductPagination();
        this.initializeProductCardListeners();
        this.initializeProductSliderListeners();
        this.initializePopupCarousel();
        this.initializeBundleATCListener();

        this.addEventListener('bundle:add', this.handleBundleAdd.bind(this));
        this.addEventListener('bundle:remove', this.handleBundleRemove.bind(this));

        // Agar user checkout complete kar ke thank_you / order status page par pahunch chuka hai,
        // to purana bundle state clear kar do taake agli dafa fresh bundle start ho.
        this.clearStateIfOrderCompleted();
    }

    getStorageKey() {
        return `bundleBuilderState:${window.location.pathname}`;
    }

    saveStateToStorage() {
        try {
            const state = {
                selectedBundleCount: this.selectedBundle?.count,
                tierFloor: this.tierFloor,
                selectedProducts: this.selectedProducts || [],
            };
            window.localStorage.setItem(this.getStorageKey(), JSON.stringify(state));
        } catch (e) {
            console.warn('BundleBuilder: unable to save state', e);
        }
    }

    loadStateFromStorage() {
        try {
            const raw = window.localStorage.getItem(this.getStorageKey());
            if (!raw) return;

            const state = JSON.parse(raw);
            if (!state || typeof state !== 'object') return;

            const { selectedBundleCount, selectedProducts, tierFloor } = state;

            if (selectedBundleCount) {
                const matchedBundle = this.bundleData.find(b => b.count === selectedBundleCount);
                if (matchedBundle) {
                    this.selectedBundle = matchedBundle;
                }
            }

            if (Array.isArray(selectedProducts)) {
                this.selectedProducts = selectedProducts
                    .filter(p => p && p.id && p.image && typeof p.price !== 'undefined');
            }
            // The floor is the tile the shopper tapped (older saved states only had the pack): the pack then follows the item count.
            this.tierFloor = this.bundleData.some(b => b.count === tierFloor) ? tierFloor : (this.selectedBundle.count || this.bundleData[0].count);
            this.applyTierForCount();
        } catch (e) {
            console.warn('BundleBuilder: unable to load state', e);
        }
    }

    clearStateFromStorage() {
        try {
            window.localStorage.removeItem(this.getStorageKey());
        } catch (e) {
            console.warn('BundleBuilder: unable to clear state', e);
        }
    }

    // Order complete hone ke baad (thank_you / order status page) state clear karo.
    // Checkout button click par state clear NAHI hoti — is se user checkout se
    // "Back" aaye to uska bundle waisa hi restore ho jata hai jaisa chhoda tha.
    clearStateIfOrderCompleted() {
        try {
            const path = window.location.pathname;
            const isOrderCompletePage = path.includes('/thank_you') || path.includes('/orders/');
            if (isOrderCompletePage) {
                this.clearStateFromStorage();
            }
        } catch (e) {
            console.warn('BundleBuilder: unable to check order completion state', e);
        }
    }

    formatMoney(amount) {
        return `${this.currencySymbol}${(amount / 100).toFixed(2)}`;
    }

    localizeDollarsToCurrency(amount) {
        return amount * window.Shopify.currency.rate;
    }

    initElements() {
        this.tabsSection = this.querySelector('#tabs-section');
        this.tabs = this.tabsSection.querySelector('.tabs');
        this.tabContents = [...this.querySelectorAll('.tab-content')];
        this.tabButtons = [...this.tabs.querySelectorAll('.tab-btn')];

        this.productSliders = this.querySelectorAll('.product-slider');

        this.bundleOptions = this.querySelector('.bundle-options');
        this.progressBars = this.querySelectorAll('.progress-fill');

        this.progressSavingsAmount = this.querySelector('.progress-savings-amount');
        this.progressStatus = this.querySelector('.progress-status');

        this.bundleData.forEach(bundle => {
            const bundleOption = this.querySelector('#bundle-template').content.cloneNode(true).querySelector('div');
            bundleOption.querySelector('.bundle-count').textContent = bundle.count;
            let price = this.formatMoney(this.localizeDollarsToCurrency(bundle.discount * 100));
            price = price.replace(/\s/g, '');
            bundleOption.querySelector('.bundle-discount').textContent = `${price} off`;

            bundleOption.dataset.count = bundle.count;
            this.bundleOptions.appendChild(bundleOption);

            bundleOption.addEventListener('click', () => {
                this.onBundleChange(bundle.count);
            });
        });

        this.originalPrice = this.querySelector('[data-original-price]');
        this.discountedPrice = this.querySelector('[data-discounted-price]');
        this.discount = this.querySelector('[data-discount]');

        this.bundleATCButton = this.querySelector('.btn_cr:not(.sticky-btn)');
        this.stickyButtonContainer = this.querySelector('.sticky-btn-container');
        this.stickyButton = this.stickyButtonContainer?.querySelector('.sticky-btn');

        this.stickyItemsAdded = this.stickyButtonContainer?.querySelector('.sticky-items-added');
        this.stickyItemsRemaining = this.stickyButtonContainer?.querySelector('.sticky-items-remaining');
        this.stickyProgressFill = this.stickyButtonContainer?.querySelector('.sticky-progress-fill');
        this.stickySavingsAmount = this.stickyButtonContainer?.querySelector('.sticky-savings-amount');
        this.stickyStatus = this.stickyButtonContainer?.querySelector('.sticky-status');
        this.stickyStatusCount = this.stickyButtonContainer?.querySelector('.sticky-status-count');
    }

    initializeBundleATCListener() {
        this.bundleATCButton.addEventListener('click', () => {
            const itemAddStrings = this.selectedProducts.map((product) => `${product.id}:1`);
            // Free-gift ladder (US): the gifts this bundle has earned ride along as $0 lines, plus the A/B tag for the scorecard.
            this.giftVariantsForCheckout().forEach((variantId) => itemAddStrings.push(`${variantId}:1`));
            let checkoutParamString = `${itemAddStrings.join(',')}?discount=${this.selectedBundle.discount_code}`;
            if (this.gift && this.gift.tag) checkoutParamString += `&attributes[fz_test]=${encodeURIComponent(this.gift.tag)}`;
            // Which landing page built this bundle (e.g. national-dog-day vs international-dog-day) — lands on the order as an attribute
            // so offer/ad tests can be read per page in Shopify. Set on every store.
            const fzPage = (window.location.pathname.split('/').filter(Boolean).pop() || 'home').slice(0, 80);
            checkoutParamString += `&attributes[fz_page]=${encodeURIComponent(fzPage)}`;
            const permalink = `/cart/${checkoutParamString}`;
            // FIX: clearStateFromStorage() yahan se hata diya gaya hai.
            // Pehle ye yahan call hota tha aur redirect se pehle hi localStorage khali kar deta tha,
            // isliye jab user checkout se "Back" aata tha to bundle empty dikhta tha (products remove ho jate the).
            // Ab state clear sirf order complete hone ke baad hoti hai (clearStateIfOrderCompleted mein),
            // is liye back aane par bundle wapis restore ho jayega.
            window.location.href = permalink;
        });
    }

    updateCardHighlights() {
        this.querySelectorAll('.product-card').forEach(card => {
            const btn = card.querySelector('.add-btn');
            if (!btn) return;
            const vid = btn.dataset.id;
            if (!vid) return;
            const count = this.selectedProducts.filter(p => String(p.id) === String(vid)).length;
            if (count > 0) {
                card.classList.add('in-bundle');
                card.setAttribute('data-bundle-count', count);
            } else {
                card.classList.remove('in-bundle');
                card.removeAttribute('data-bundle-count');
            }
        });
    }

    updateUI() {
        const progressBarWidth = (this.selectedProducts.length / this.selectedBundle.count) * 100;
        this.progressBars.forEach(pb => pb.style.width = `${progressBarWidth}%`);

        const itemsAdded = this.selectedProducts.length;
        const itemsRemaining = this.selectedBundle.count - itemsAdded;
        const isBundleComplete = itemsAdded === this.selectedBundle.count;
        const nextTier = this.bundleData.find(b => b.count > this.selectedBundle.count) || null;
        const isBoxFull = isBundleComplete && !nextTier;
        const upHint = isBundleComplete && nextTier ? `<span class="fz-bb-up">Add 1 more and you move up to the ${this.packLabel(nextTier)} (${this.discountLabel(nextTier)} off)</span>` : '';

        if (this.progressSavingsAmount) {
            const savingsAmount = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            this.progressSavingsAmount.textContent = this.formatMoney(savingsAmount);
        }
        if (this.progressStatus) {
            if (isBundleComplete) {
                this.progressStatus.innerHTML = 'Bundle complete! Ready to checkout' + upHint;
                this.progressStatus.classList.add('complete');
            } else {
                const progressPercent = Math.round(progressBarWidth);
                this.progressStatus.innerHTML = `Add <span class="progress-status-count">${itemsRemaining}</span> more to complete your bundle <span class="clr_discount">(${progressPercent}% done)</span>`;
                this.progressStatus.classList.remove('complete');
            }
        }

        const placeholderImage = this.querySelector('template[data-placeholder-image]').content.cloneNode(true).querySelector('img');
        const imagesContainer = this.querySelector('.curve-images');

        imagesContainer.innerHTML = '';

        this.selectedProducts.forEach(product => {
            const img = document.createElement('img');
            img.src = product.image;

            const crossButton = document.createElement('button');
            crossButton.classList.add('cross-btn');
            crossButton.innerHTML = '×';
            crossButton.addEventListener('click', () => {
                this.dispatchEvent(new CustomEvent('bundle:remove', {
                    detail: { variantId: product.id }
                }));
            });

            const imgContainer = document.createElement('div');
            imgContainer.classList.add('img-container');
            imgContainer.appendChild(img);
            imgContainer.appendChild(crossButton);

            imagesContainer.appendChild(imgContainer);
        });

        for (let i = this.selectedProducts.length; i < this.selectedBundle.count; i++) {
            imagesContainer.appendChild(placeholderImage.cloneNode(true));
        }

        this.bundleOptions.querySelectorAll('.bundle').forEach(bundle => {
            bundle.classList.remove('active');
        });
        this.bundleOptions.querySelector(`[data-count="${this.selectedBundle.count}"]`).classList.add('active');

        const totalPrice = this.selectedProducts.reduce((acc, product) => acc + +product.price, 0);

        this.discount.textContent = this.formatMoney(this.localizeDollarsToCurrency(this.selectedBundle.discount * 100));

        if (totalPrice === 0) {
            this.originalPrice.textContent = '';
            this.discountedPrice.textContent = '';
        } else {
            this.originalPrice.textContent = this.formatMoney(totalPrice);
            const bundleDiscountLocalized = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            const discountedPrice = totalPrice - bundleDiscountLocalized;
            this.discountedPrice.textContent = this.formatMoney(discountedPrice);
        }

        if (isBundleComplete) {
            this.bundleATCButton.disabled = false;
            this.bundleATCButton.classList.remove('disabled');
            if (this.stickyButton) {
                this.stickyButton.disabled = false;
                this.stickyButton.classList.remove('disabled');
            }
            if (isBoxFull) {
                this.querySelectorAll('.add-btn').forEach(btn => {
                    btn.disabled = true;
                    btn.classList.add('disabled', 'is-full');
                    btn.textContent = 'Box is full';
                });
            } else {
                // A full pack that can still grow keeps Add alive: the next item upgrades the pack.
                this.querySelectorAll('.add-btn').forEach(btn => btn.classList.remove('is-full'));
                this.querySelectorAll('.product-card').forEach(productCard => {
                    this.onProductChange(productCard);
                });
            }
        } else {
            this.bundleATCButton.disabled = true;
            this.bundleATCButton.classList.add('disabled');
            if (this.stickyButton) {
                this.stickyButton.disabled = true;
                this.stickyButton.classList.add('disabled');
            }
            this.querySelectorAll('.add-btn').forEach(btn => btn.classList.remove('is-full'));
            this.querySelectorAll('.product-card').forEach(productCard => {
                this.onProductChange(productCard);
            });
        }

        if (this.stickyItemsAdded) {
            this.stickyItemsAdded.textContent = `${itemsAdded} item${itemsAdded !== 1 ? 's' : ''} added`;
        }
        if (this.stickyItemsRemaining) {
            if (isBundleComplete) {
                this.stickyItemsRemaining.textContent = 'Bundle complete!';
                this.stickyItemsRemaining.classList.add('complete');
            } else {
                this.stickyItemsRemaining.textContent = `${itemsRemaining} remaining`;
                this.stickyItemsRemaining.classList.remove('complete');
            }
        }
        if (this.stickyProgressFill) {
            const progressPercent = (itemsAdded / this.selectedBundle.count) * 100;
            this.stickyProgressFill.style.width = `${progressPercent}%`;
        }

        if (this.stickySavingsAmount) {
            const savingsAmount = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            this.stickySavingsAmount.textContent = this.formatMoney(savingsAmount);
        }
        if (this.stickyStatus) {
            if (isBundleComplete) {
                this.stickyStatus.textContent = 'Bundle complete! Ready to checkout';
                this.stickyStatus.classList.add('complete');
            } else {
                const progressPercent = Math.round((itemsAdded / this.selectedBundle.count) * 100);
                this.stickyStatus.innerHTML = `Add <span class="sticky-status-count">${itemsRemaining}</span> more to complete your bundle (${progressPercent}% done)`;
                this.stickyStatus.classList.remove('complete');
            }
        }

        this.updateCardHighlights();
        this.updateGiftLadder();
        this.saveStateToStorage();
    }

    onBundleChange(count) {
        this.tierFloor = count;
        this.hideTierToast();
        if (count === this.selectedBundle.count) return;

        this.selectedBundle = this.bundleData.find(bundle => bundle.count === count);

        if (this.selectedProducts.length > count) {
            this.selectedProducts = this.selectedProducts.slice(0, count);
        }

        this.updateUI();
    }

    handleBundleAdd(e) {
        const { variantId, count, image, price } = e.detail;

        const postTotal = this.selectedProducts.length + count;

        if (postTotal > this.maxBundleCount()) {
            this.showTierToast(`Your box is full: the <b>${this.packLabel(this.selectedBundle)}</b> holds ${this.maxBundleCount()} items. Remove one to swap.`);
            return;
        }

        for (let i = 0; i < count; i++) {
            this.selectedProducts.push({
                id: variantId,
                image: image,
                price: price
            });
        }

        const upgradedFrom = this.applyTierForCount();

        // Card find karo
        const addedCard = [...this.querySelectorAll('.product-card')].find(card => {
            return card.querySelector('.add-btn')?.dataset.id == variantId;
        });

        if (addedCard) {
            const qty = this.selectedProducts.filter(p => String(p.id) === String(variantId)).length;
            addedCard.classList.add('in-bundle');
            addedCard.setAttribute('data-bundle-count', qty);

            // Card ko us tab ke product-grid mein PEHLE move karo
            const parentGrid = addedCard.closest('.product-grid');
            if (parentGrid && parentGrid.firstChild !== addedCard) {
                parentGrid.prepend(addedCard);
            }
        }

        this.updateUI();

        if (upgradedFrom) {
            const b = this.selectedBundle, remaining = b.count - this.selectedProducts.length;
            this.showTierToast(
                `Moved up to the <b>${this.packLabel(b)}</b> · <b>${this.discountLabel(b)} off</b>. ` + (remaining > 0 ? `Add ${remaining} more to complete it.` : 'Bundle complete!'),
                () => { for (let i = 0; i < count; i++) this.dispatchEvent(new CustomEvent('bundle:remove', { detail: { variantId, silent: true } })); }
            );
        }
    }

    handleBundleRemove(e) {
        const variantId = e.detail.variantId;
        let bundleToRemove = -1;
        for (let i = this.selectedProducts.length - 1; i >= 0; i--) {
            if (this.selectedProducts[i].id === variantId) {
                bundleToRemove = i;
                break;
            }
        }

        if (bundleToRemove !== -1) {
            this.selectedProducts.splice(bundleToRemove, 1);

            const downgradedFrom = this.applyTierForCount();
            if (downgradedFrom && !e.detail.silent) {
                this.showTierToast(`Back to the <b>${this.packLabel(this.selectedBundle)}</b> · <b>${this.discountLabel(this.selectedBundle)} off</b>`);
            }

            const removedCard = [...this.querySelectorAll('.product-card')].find(card => {
                return card.querySelector('.add-btn')?.dataset.id == variantId;
            });
            if (removedCard) {
                const remaining = this.selectedProducts.filter(p => String(p.id) === String(variantId)).length;
                if (remaining === 0) {
                    removedCard.classList.remove('in-bundle');
                    removedCard.removeAttribute('data-bundle-count');

                    // Remove ke baad: saare in-bundle cards ko top pe lao
                    const parentGrid = removedCard.closest('.product-grid');
                    if (parentGrid) {
                        const inBundleCards = [...parentGrid.querySelectorAll('.product-card.in-bundle')];
                        inBundleCards.reverse().forEach(card => {
                            parentGrid.prepend(card);
                        });
                    }
                } else {
                    removedCard.setAttribute('data-bundle-count', remaining);
                }
            }

            this.updateUI();
        }
    }

    initializeTabStickyListener() {
        const tabsOffset = this.tabsSection.offsetTop;

        window.addEventListener('scroll', () => {
            if (window.scrollY >= tabsOffset) {
                this.tabs.classList.add('is-sticky');
            } else {
                this.tabs.classList.remove('is-sticky');
            }
        });
    }

    initializeStickyButtonListener() {
        if (!this.bundleATCButton || !this.stickyButtonContainer || !this.stickyButton) return;

        const syncStickyButtonState = () => {
            if (this.bundleATCButton.disabled) {
                this.stickyButton.disabled = true;
                this.stickyButton.classList.add('disabled');
            } else {
                this.stickyButton.disabled = false;
                this.stickyButton.classList.remove('disabled');
            }
        };

        this.stickyButton.addEventListener('click', () => {
            this.bundleATCButton.click();
        });

        window.addEventListener('scroll', () => {
            const buttonRect = this.bundleATCButton.getBoundingClientRect();
            if (buttonRect.bottom < 0) {
                this.stickyButtonContainer.classList.add('mobile-visible');
                syncStickyButtonState();
            } else {
                this.stickyButtonContainer.classList.remove('mobile-visible');
            }
        });

        syncStickyButtonState();
    }

    initializeTabListeners() {
        this.tabButtons.forEach(button => {
            button.addEventListener('click', () => {
                this.querySelectorAll('.tab-btn, .tab-content').forEach((el) => el.classList.remove('active'));
                button.classList.add('active');

                const activeTab = this.querySelector(`#${button.dataset.tab}`);
                activeTab.classList.add('active');

                const products = activeTab.querySelectorAll('.product-card');
                const loadBtn = activeTab.querySelector('.loadmore-btn');
                const initialVisibleCount = 8;
                this.toggleProductVisibility(products, initialVisibleCount, loadBtn);

                const headerOffset = window.innerWidth < 768 ? 80 : 120;
                const scrollTarget = activeTab.getBoundingClientRect().top + window.pageYOffset - headerOffset - 60;

                window.scrollTo({
                    top: Math.max(0, scrollTarget),
                    behavior: 'smooth',
                });

                this.updateCardHighlights();
            });
        });
    }

    moveSliderTo(slider, index) {
        const track = slider.querySelector('.slider-track');
        track.style.transform = 'translateX(-' + index * 100 + '%)';
    }

    initializeProductSliderListeners() {
        this.productSliders.forEach(slider => {
            const slides = slider.querySelectorAll('.slide');
            const prev = slider.querySelector('.left');
            const next = slider.querySelector('.right');
            if (!slides.length || !prev || !next) return;
            let index = 0;

            next.addEventListener('click', (e) => {
                e.preventDefault();
                index = (index + 1) % slides.length;
                this.moveSliderTo(slider, index);
            });

            prev.addEventListener('click', (e) => {
                e.preventDefault();
                index = (index - 1 + slides.length) % slides.length;
                this.moveSliderTo(slider, index);
            });
        });
    }

    toggleProductVisibility(products, visibleCount, loadBtn) {
        products.forEach((card, index) => {
            if (index < visibleCount) {
                card.classList.add('visible');
            }
        });

        if (visibleCount >= products.length) {
            loadBtn.classList.add('hide');
        }
    }

    initProductPagination() {
        for (const tab of this.tabContents) {
            const products = tab.querySelectorAll('.product-card');

            products.forEach(product => {
                const firstColor = product.querySelector('.color-swatch');
                if (firstColor) {
                    firstColor.classList.add('active');
                }

                const firstSize = product.querySelector('.size-swatch');
                if (firstSize) {
                    firstSize.classList.add('active');
                }

                this.onProductChange(product);
            });

            const loadBtn = tab.querySelector('.loadmore-btn');
            let visibleCount = 8;

            this.toggleProductVisibility(products, visibleCount, loadBtn);

            loadBtn.addEventListener('click', (e) => {
                e.preventDefault();
                visibleCount += 8;
                this.toggleProductVisibility(products, visibleCount, loadBtn);
                this.updateCardHighlights();
            });
        }
    }

    // =====================================================================
    // Free-gift ladder (US only). The cart drawer's script (snippets/gift-ladder.liquid) publishes its tiers, A/B arm,
    // sold-out flags and "customer removed this gift" flags on window.fzGift. The builder never keeps its own copy of the
    // rules: it draws the same ladder inside the bundle summary and, on Checkout, appends the earned gift variants to the
    // cart permalink (so the gifts show as $0 lines in checkout without a detour through the cart drawer).
    // "Paid" = bundle subtotal minus the bundle discount, the same rule the drawer applies to discounted carts.
    // Stores without the drawer script (UK/EU) get no ladder and no gifts: everything below is a no-op when window.fzGift is absent.
    // =====================================================================
    initGiftLadder() {
        const g = window.fzGift;
        this.gift = g && Array.isArray(g.tiers) && g.tiers.length ? g : null;
        if (!this.gift) return;

        this._ensureGiftStyles();

        const summary = this.querySelector('.box-summary');
        if (summary && !summary.querySelector('.fz-bb-gift')) {
            const box = document.createElement('div');
            box.className = 'fz-bb-gift';
            const anchor = this.bundleOptions && this.bundleOptions.parentElement === summary ? this.bundleOptions : null;
            if (anchor) summary.insertBefore(box, anchor.nextSibling); else summary.appendChild(box);
        }
        this.giftBox = summary ? summary.querySelector('.fz-bb-gift') : null;

        if (this.bundleATCButton && !this.bundleATCButton.parentElement.querySelector('.fz-bb-gift-note')) {
            const note = document.createElement('p');
            note.className = 'fz-bb-gift-note';
            this.bundleATCButton.insertAdjacentElement('afterend', note);
        }
        this.giftNote = this.bundleATCButton ? this.bundleATCButton.parentElement.querySelector('.fz-bb-gift-note') : null;

        const stickySavings = this.stickyButtonContainer?.querySelector('.sticky-savings');
        if (stickySavings && !stickySavings.querySelector('.fz-bb-gift-chip')) {
            const chip = document.createElement('span');
            chip.className = 'fz-bb-gift-chip';
            stickySavings.appendChild(chip);
        }
        this.giftChip = stickySavings ? stickySavings.querySelector('.fz-bb-gift-chip') : null;

        document.addEventListener('fz:gift-stock', () => this.updateGiftLadder());
    }

    giftPaidCents() {
        const total = this.selectedProducts.reduce((acc, product) => acc + +product.price, 0);
        // Only the discount of a pack the shopper has actually filled is subtracted, so adding an item never locks a gift again.
        const discount = this.localizeDollarsToCurrency(this.earnedDiscount() * 100);
        return Math.max(0, Math.round(total - discount));
    }

    // UK/EU ladders count toys ("2 toys", "3 toys"); the US ladder counts money. A tier with .n is a toy-count tier.
    giftToyCount() {
        const g = this.gift;
        if (!g || !Array.isArray(g.eligible)) return 0;
        const ok = {}; g.eligible.forEach((h) => { ok[h] = 1; });
        const map = window.fzBBHandles || {};
        // A variant the map does not know (page HTML cached from before the map existed) counts as a toy:
        // the builder only lists the Freezbones collection, which is the gift-eligible list.
        return this.selectedProducts.filter((p) => { const h = map[String(p.id)]; return h === undefined ? true : !!ok[h]; }).length;
    }

    giftTierUnlocked(tier, paid) {
        if (tier.soldOut) return false;
        return tier.n ? this.giftToyCount() >= tier.n : paid >= tier.amt;
    }

    giftVariantsForCheckout() {
        if (!this.gift) return [];
        const paid = this.giftPaidCents();
        return this.gift.tiers
            // The box page tells the shopper these gifts "go with this bundle", so it always sends every earned gift.
            // (It used to skip a gift the shopper had removed from the cart earlier in the same tab, while still promising it.)
            .filter((t) => t.kind === 'gift' && t.variant && this.giftTierUnlocked(t, paid))
            .map((t) => t.variant);
    }

    updateGiftLadder() {
        if (!this.gift) return;
        const tiers = this.gift.tiers;
        const paid = this.giftPaidCents();
        const money = (c) => (this.gift.money ? this.gift.money(c) : '$' + (c / 100).toFixed(2));
        const countMode = tiers.some((t) => t.n);
        const gap = (t) => (t.n ? `${Math.max(1, t.n - this.giftToyCount())} more toy${t.n - this.giftToyCount() > 1 ? 's' : ''}` : money(t.amt - paid));
        const ladderLine = countMode ? tiers.map((t) => `${t.kind === 'ship' ? 'Free shipping' : 'Free ' + t.name} at ${t.label}`).join(' · ') : 'Free gifts at $39, $79 and $119. Free shipping at $59.';
        const esc = (s) => String(s).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

        let next = null;
        for (let i = 0; i < tiers.length; i++) { if (!tiers[i].soldOut && !this.giftTierUnlocked(tiers[i], paid)) { next = tiers[i]; break; } }
        let last = null;
        for (let j = tiers.length - 1; j >= 0; j--) { if (this.giftTierUnlocked(tiers[j], paid)) { last = tiers[j]; break; } }

        // Same words as the cart drawer, so the two ladders read as one.
        let h, s;
        if (!last) {
            h = `Add <b>${gap(next)}</b>${next.n ? '' : ' more'} to unlock ${next.kind === 'ship' ? '<b>free shipping</b>' : `a <b>FREE ${esc(next.name)}</b>`}`;
            s = ladderLine;
        } else if (next) {
            h = last.kind === 'ship' ? 'Free shipping unlocked!' : `You unlocked a <b>FREE ${esc(last.name)}</b>!`;
            s = `Add <b>${gap(next)}</b>${next.n ? ' ' : ' more '}` + (next.kind === 'ship' ? 'for free shipping' : `to unlock a <b>FREE ${esc(next.name)}</b>`);
        } else {
            h = 'You unlocked <b>everything</b>. Nice.';
            s = 'Your free gifts and free shipping go with this bundle';
        }

        const POS = [25, 50, 75, 100];
        let fill = 0, prevA = 0, prevP = 0;
        for (let k = 0; k < tiers.length; k++) {
            const t = tiers[k], p = POS[k] || 100;
            if (countMode) { if (!this.giftTierUnlocked(t, paid)) break; fill = p; continue; }
            if (paid < t.amt) { fill = prevP + (paid - prevA) / (t.amt - prevA) * (p - prevP); break; }
            prevA = t.amt; prevP = p; fill = 100;
        }

        const TRUCK = '<svg viewBox="0 0 24 24"><path d="M1 8h13v9H1zM14 11h4l3 3v3h-7z"/><circle cx="5.5" cy="17.5" r="1.8"/><circle cx="17.5" cy="17.5" r="1.8"/></svg>';
        const CHECK = '<span class="fz-ck"><svg viewBox="0 0 12 12"><path d="M2 6.5l2.6 2.5L10 3.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
        const nodes = tiers.map((t, k) => {
            const on = this.giftTierUnlocked(t, paid);
            const cls = t.soldOut ? ' out' : (on ? ' on' : (next === t ? ' next' : ''));
            const inner = t.kind === 'ship' ? TRUCK : `<img src="${esc(t.img)}" alt="">`;
            return `<div class="fz-node${cls}" style="left:${POS[k] || 100}%"><span class="fz-lbl">${esc(t.label)}</span><span class="fz-dot">${inner}${CHECK}</span><span class="fz-thr">${t.soldOut ? 'Sold out' : esc(t.thr)}</span></div>`;
        }).join('');

        if (this.giftBox) {
            this.giftBox.innerHTML = `<p class="fz-h">${h}</p><p class="fz-s">${s}</p><div class="fz-track"><div class="fz-rail"></div><div class="fz-fill" style="width:${fill.toFixed(2)}%"></div>${nodes}</div>`;
        }

        const gifts = tiers.filter((t) => t.kind === 'gift' && this.giftTierUnlocked(t, paid)).length;
        const ship = tiers.some((t) => t.kind === 'ship' && paid >= t.amt);
        const isBundleComplete = this.selectedProducts.length === this.selectedBundle.count;
        let summary = '';
        if (gifts || ship) {
            const parts = [];
            if (gifts) parts.push(`${gifts} free gift${gifts !== 1 ? 's' : ''}`);
            if (ship) parts.push('free shipping');
            summary = parts.join(' + ');
        }
        if (this.giftNote) {
            this.giftNote.innerHTML = summary && isBundleComplete
                ? `<b>${esc(summary)}</b> go with this bundle`
                : 'Free gifts are added to your checkout automatically';
        }
        if (this.giftChip) {
            this.giftChip.textContent = summary ? `🎁 ${summary}` : '';
            this.giftChip.style.display = summary ? '' : 'none';
        }
    }

    _ensureGiftStyles() {
        if (document.getElementById('fz-bb-gift-styles')) return;
        const style = document.createElement('style');
        style.id = 'fz-bb-gift-styles';
        style.textContent = [
            /* Desktop-first: the summary column is ~700-800px wide, so the ladder fills it and matches the pack tiles' scale. */
            '.fz-bb-gift{position:relative;border:1px solid #F3E3D3;background:#FFF9F4;border-radius:14px;padding:22px 24px 16px;margin:0 auto 28px;max-width:640px;text-align:center;font-family:inherit;color:#111;box-sizing:border-box;}',
            '.fz-bb-gift .fz-h{font-size:22px;font-weight:800;line-height:1.25;margin:0;}',
            '.fz-bb-gift .fz-h b{color:#F74E00;}',
            '.fz-bb-gift .fz-s{font-size:14.5px;font-weight:700;line-height:1.3;margin:6px 0 18px;opacity:.65;}',
            '.fz-bb-gift .fz-s b{font-weight:800;}',
            '.fz-bb-gift .fz-track{position:relative;height:112px;margin:0 44px 0 16px;}',
            '.fz-bb-gift .fz-rail{position:absolute;left:0;right:0;top:46px;height:7px;background:#E6E6E6;border-radius:999px;}',
            '.fz-bb-gift .fz-fill{position:absolute;left:0;top:46px;height:7px;background:#F74E00;border-radius:999px;transition:width .4s ease;display:block!important;}',
            '.fz-bb-gift .fz-node{position:absolute;top:0;width:0;text-align:center;}',
            '.fz-bb-gift .fz-lbl{position:absolute;left:50%;transform:translateX(-50%);top:0;white-space:nowrap;font-size:14px;line-height:1;font-weight:800;color:#111;}',
            '.fz-bb-gift .fz-dot{position:absolute;left:50%;top:22px;transform:translateX(-50%);width:54px;height:54px;border-radius:50%;background:#fff;border:2.5px solid #D9D9D9;box-sizing:border-box;display:flex;align-items:center;justify-content:center;}',
            '.fz-bb-gift .fz-dot img{width:44px;height:44px;border-radius:50%;object-fit:cover;opacity:.5;filter:grayscale(.7);margin:0;max-width:none;}',
            '.fz-bb-gift .fz-dot svg{width:28px;height:28px;stroke:#111;fill:none;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round;}',
            '.fz-bb-gift .fz-thr{position:absolute;left:50%;transform:translateX(-50%);top:84px;white-space:nowrap;font-size:12.5px;line-height:1;font-weight:700;color:#111;opacity:.6;}',
            '.fz-bb-gift .fz-ck{position:absolute;right:-6px;bottom:-4px;width:20px;height:20px;border-radius:50%;background:#F74E00;border:2.5px solid #fff;display:none;align-items:center;justify-content:center;}',
            '.fz-bb-gift .fz-node .fz-dot .fz-ck svg,.fz-bb-gift .fz-node.on .fz-dot .fz-ck svg{width:12px;height:12px;stroke:#fff !important;stroke-width:2.6;fill:none;}',
            '.fz-bb-gift .fz-node.on .fz-dot{border-color:#F74E00;}',
            '.fz-bb-gift .fz-node.on .fz-dot img{opacity:1;filter:none;}',
            '.fz-bb-gift .fz-node.on .fz-dot svg{stroke:#F74E00;}',
            '.fz-bb-gift .fz-node.on .fz-lbl,.fz-bb-gift .fz-node.on .fz-thr{color:#F74E00;opacity:1;}',
            '.fz-bb-gift .fz-node.on .fz-ck{display:flex;}',
            '.fz-bb-gift .fz-node.next .fz-dot{box-shadow:0 0 0 5px rgba(247,78,0,.18);animation:fzBbPulse 1.6s ease-in-out infinite;}',
            '.fz-bb-gift .fz-node.next .fz-dot img{opacity:.85;filter:grayscale(.2);}',
            '.fz-bb-gift .fz-node.out .fz-dot{border-style:dashed;}',
            '.fz-bb-gift .fz-node.out .fz-dot img{opacity:.35;filter:grayscale(1);}',
            '.fz-bb-gift .fz-node.out .fz-thr{text-decoration:line-through;}',
            '@keyframes fzBbPulse{0%,100%{transform:translateX(-50%) scale(1)}50%{transform:translateX(-50%) scale(1.08)}}',
            '@media (prefers-reduced-motion:reduce){.fz-bb-gift .fz-node.next .fz-dot{animation:none;}}',
            '.fz-bb-gift-note{font-size:14px;color:#555;margin:10px 0 0;text-align:center;}',
            '.fz-bb-gift-note b{color:#111;font-weight:800;}',
            '.fz-bb-gift-chip{display:inline-block;margin-left:8px;background:#FFF1E6;color:#B34A00;border-radius:999px;padding:2px 9px;font-size:11px;font-weight:700;white-space:nowrap;}',
            /* Tablet: the summary column narrows */
            '@media (max-width:989px){.fz-bb-gift{padding:16px 14px 12px;margin-bottom:22px;}.fz-bb-gift .fz-h{font-size:17px;}.fz-bb-gift .fz-s{font-size:12.5px;margin-bottom:12px;}.fz-bb-gift .fz-track{height:90px;margin:0 34px 0 10px;}.fz-bb-gift .fz-rail,.fz-bb-gift .fz-fill{top:36px;height:6px;}.fz-bb-gift .fz-lbl{font-size:11.5px;}.fz-bb-gift .fz-dot{top:16px;width:42px;height:42px;}.fz-bb-gift .fz-dot img{width:34px;height:34px;}.fz-bb-gift .fz-dot svg{width:22px;height:22px;}.fz-bb-gift .fz-thr{top:66px;font-size:10.5px;}.fz-bb-gift .fz-ck{width:16px;height:16px;}.fz-bb-gift .fz-ck svg{width:9px;height:9px;}.fz-bb-gift-note{font-size:12px;}}',
            /* Phone: compact, same proportions as the cart drawer */
            '@media (max-width:749px){.fz-bb-gift{padding:12px 10px 8px;margin-bottom:16px;border-radius:10px;}.fz-bb-gift .fz-h{font-size:14px;}.fz-bb-gift .fz-s{font-size:11.5px;margin:3px 0 10px;}.fz-bb-gift .fz-track{height:76px;margin:0 28px 0 8px;}.fz-bb-gift .fz-rail,.fz-bb-gift .fz-fill{top:30px;height:5px;}.fz-bb-gift .fz-lbl{font-size:10.5px;}.fz-bb-gift .fz-dot{top:14px;width:36px;height:36px;border-width:2px;}.fz-bb-gift .fz-dot img{width:28px;height:28px;}.fz-bb-gift .fz-dot svg{width:19px;height:19px;}.fz-bb-gift .fz-thr{top:56px;font-size:9.5px;}.fz-bb-gift .fz-ck{width:14px;height:14px;border-width:2px;}.fz-bb-gift .fz-ck svg{width:8px;height:8px;}.fz-bb-gift-note{font-size:11.5px;}}',
        ].join('\n');
        document.head.appendChild(style);
    }

    _ensurePriceStyles() {
        if (document.getElementById('bundle-card-price-styles')) return;

        const style = document.createElement('style');
        style.id = 'bundle-card-price-styles';
        // Sized to fit the narrowest card (135px of usable width in the 3-up
        // desktop grid) on one line, including the widest price in any catalogue
        // (EU's \u20ac92,00 / \u20ac123,00 is the worst case at 126px of 135px available).
        style.textContent =
            '.card-bottom > p{display:flex;justify-content:center;align-items:baseline;gap:2px;flex-wrap:wrap;}' +
            '.card-bottom > p .card-price__sale{font-size:15px;font-weight:700;color:#121212;}' +
            '.card-bottom > p .card-price__was{font-size:11.5px;color:#8a8a8a;text-decoration:line-through;}' +
            '.card-bottom > p .card-price__badge{padding:1px 3px;border-radius:999px;background:#FF6A00;' +
            'color:#fff;font-size:9px;font-weight:700;white-space:nowrap;align-self:center;}';
        document.head.appendChild(style);
    }

    // Formats like formatMoney(), but keeps the store's own decimal separator.
    // EU renders "\u20ac15,95" in Liquid while formatMoney() would emit "\u20ac15.95".
    _formatCardMoney(cents) {
        const formatted = this.formatMoney(cents);
        return this._decimalSeparator === ',' ? formatted.replace(/\.(\d{2})$/, ',$1') : formatted;
    }

    updateCardPrice(productCard, variant) {
        if (!productCard || !variant || variant.price == null) return;

        const priceEl = productCard.querySelector('.card-bottom .card-price')
            || productCard.querySelector('.card-bottom > p');
        if (!priceEl) return;

        // Learn the separator once, from the price Liquid rendered before we overwrite it.
        if (this._decimalSeparator === undefined) {
            this._decimalSeparator = /\d,\d{2}(?!\d)/.test(priceEl.textContent) ? ',' : '.';
        }

        const compareAt = variant.compare_at_price;

        if (!(compareAt && compareAt > variant.price)) {
            priceEl.textContent = this._formatCardMoney(variant.price);
            return;
        }

        this._ensurePriceStyles();

        const percent = Math.round((1 - variant.price / compareAt) * 100);

        priceEl.innerHTML =
            '<span class="card-price__sale">' + this._formatCardMoney(variant.price) + '</span>' +
            '<s class="card-price__was">' + this._formatCardMoney(compareAt) + '</s>' +
            '<span class="card-price__badge">-' + percent + '%</span>';
    }

    // =====================================================================
    // Count-driven pack size (Sep 2026). The tier follows the number of items in the box: adding a 6th item to a full
    // 5-pack moves the shopper up to the 9-pack, removing it drops them back. The tile they tapped is a floor, never a cap.
    // =====================================================================
    maxBundleCount() {
        return this.bundleData[this.bundleData.length - 1].count;
    }

    tierForCount(count) {
        const floor = this.tierFloor || this.bundleData[0].count;
        const fit = this.bundleData.find(b => b.count >= count);
        const want = Math.max(floor, fit ? fit.count : this.maxBundleCount());
        return this.bundleData.find(b => b.count === want) || this.bundleData[this.bundleData.length - 1];
    }

    // Re-derive the pack from the item count. Returns the previous pack when the tier changed, otherwise null.
    applyTierForCount() {
        const before = this.selectedBundle;
        const tier = this.tierForCount(this.selectedProducts.length);
        if (tier.count === before.count) return null;
        this.selectedBundle = tier;
        return before;
    }

    // Discount already banked: the biggest pack the shopper has completely filled (0 under the first tier).
    earnedDiscount() {
        const n = this.selectedProducts.length;
        let earned = 0;
        this.bundleData.forEach(b => { if (n >= b.count) earned = b.discount; });
        return earned;
    }

    packLabel(bundle) {
        return `${bundle.count}-pack`;
    }

    discountLabel(bundle) {
        return this.formatMoney(this.localizeDollarsToCurrency(bundle.discount * 100)).replace(/\s/g, '');
    }

    showTierToast(html, undo) {
        this._ensureTierStyles();
        let toast = document.querySelector('.fz-bb-toast');
        if (!toast) { toast = document.createElement('div'); toast.className = 'fz-bb-toast'; document.body.appendChild(toast); }
        clearTimeout(this._toastTimer);
        toast.innerHTML = `<span class="fz-bb-toast__txt">${html}</span>` + (undo ? '<button type="button" class="fz-bb-toast__undo">Undo</button>' : '') + '<button type="button" class="fz-bb-toast__x" aria-label="Dismiss">&times;</button>';
        const hide = () => { toast.classList.remove('is-on'); };
        toast.querySelector('.fz-bb-toast__x').addEventListener('click', hide);
        const u = toast.querySelector('.fz-bb-toast__undo');
        if (u) u.addEventListener('click', () => { hide(); undo(); });
        requestAnimationFrame(() => toast.classList.add('is-on'));
        this._toastTimer = setTimeout(hide, 7000);
    }

    hideTierToast() {
        const toast = document.querySelector('.fz-bb-toast');
        if (toast) toast.classList.remove('is-on');
        clearTimeout(this._toastTimer);
    }

    _ensureTierStyles() {
        if (document.getElementById('fz-bb-tier-styles')) return;
        const style = document.createElement('style');
        style.id = 'fz-bb-tier-styles';
        style.textContent = [
            '.fz-bb-toast{position:fixed;left:50%;top:96px;transform:translate(-50%,-12px);z-index:2147483000;display:flex;align-items:center;gap:10px;max-width:min(92vw,460px);padding:12px 12px 12px 16px;background:#111;color:#fff;border-radius:12px;box-shadow:0 10px 30px rgba(0,0,0,.25);font-family:inherit;font-size:14px;line-height:1.35;opacity:0;pointer-events:none;transition:opacity .25s ease,transform .25s ease;box-sizing:border-box;}',
            '.fz-bb-toast.is-on{opacity:1;pointer-events:auto;transform:translate(-50%,0);}',
            '.fz-bb-toast b{color:#FF9A5C;font-weight:800;}',
            '.fz-bb-toast__txt{flex:1;}',
            '.fz-bb-toast__undo{flex:none;background:#F74E00;color:#fff;border:0;border-radius:8px;padding:8px 14px;font-weight:800;font-size:13px;cursor:pointer;font-family:inherit;}',
            '.fz-bb-toast__x{flex:none;background:transparent;color:#fff;border:0;font-size:20px;line-height:1;padding:0 2px;cursor:pointer;opacity:.7;}',
            '@media (max-width:749px){.fz-bb-toast{top:auto;bottom:150px;width:calc(100vw - 24px);font-size:13px;}}',
            '.progress-status .fz-bb-up,.sticky-status .fz-bb-up{display:block;font-size:.85em;font-weight:600;opacity:.75;margin-top:2px;}',
            '.add-btn.is-full{cursor:not-allowed;}',
        ].join('\n');
        document.head.appendChild(style);
    }

    onProductChange(productCard) {
        const self = this;

        function updateATCButton(atcBtn, variant) {
            self.updateCardPrice(productCard, variant);

            if (variant && variant.available) {
                atcBtn.disabled = false;
                atcBtn.classList.remove('disabled');
                atcBtn.textContent = atcBtn.getAttribute('data-available');
                atcBtn.dataset.id = variant.id;
                atcBtn.dataset.price = variant.price;
            } else {
                atcBtn.disabled = true;
                atcBtn.classList.add('disabled');
                atcBtn.textContent = atcBtn.getAttribute('data-out-of-stock');
                atcBtn.dataset.id = '';
                atcBtn.dataset.price = '';
            }
        }

        const activeColorSwatch = productCard.querySelector('.color-swatch.active');
        const activeSizeSwatch = productCard.querySelector('.size-swatch.active');

        const atcBtn = productCard.querySelector('.add-btn');
        const productData = JSON.parse(productCard.querySelector('script[type="application/json"]').innerHTML);

        if (!activeColorSwatch && !activeSizeSwatch) {
            if (productData.variants.length !== 1) {
                atcBtn.disabled = true;
                atcBtn.classList.add('disabled');
                atcBtn.textContent = 'Select options';
                return;
            } else {
                const variant = productData.variants[0];
                updateATCButton(atcBtn, variant);
                return;
            }
        }

        const colorValue = activeColorSwatch?.dataset?.value || null;
        const colorOptionIndex = activeColorSwatch?.dataset?.optionIndex || '';

        const sizeValue = activeSizeSwatch?.dataset?.value || null;
        const sizeOptionIndex = activeSizeSwatch?.dataset?.optionIndex || '';

        const variant = productData.variants.find(v =>
            (!colorOptionIndex || v[`option${colorOptionIndex}`] === colorValue) &&
            (!sizeOptionIndex || v[`option${sizeOptionIndex}`] === sizeValue)
        );

        const productSlider = productCard.querySelector('.product-slider');

        if (variant) {
            const productCardImages = [...productCard.querySelectorAll('.popup-img')];
            const variantImageIndex = productCardImages.findIndex(img => img.dataset.variants.includes(variant.id));

            if (variantImageIndex !== -1) {
                this.moveSliderTo(productSlider, variantImageIndex);
            }
        }

        updateATCButton(atcBtn, variant);
    }

    initializeProductCardListeners() {
        this.addEventListener('click', (e) => {
            if (e.target.classList.contains('color-swatch')) {
                const group = e.target.closest('.color-swatches');
                group.querySelectorAll('.color-swatch').forEach((s) => s.classList.remove('active'));
                e.target.classList.add('active');
            }

            if (e.target.classList.contains('size-swatch')) {
                const group = e.target.closest('.size-swatches');
                group.querySelectorAll('.size-swatch').forEach((s) => s.classList.remove('active'));
                e.target.classList.add('active');
            }

            if (e.target.classList.contains('plus')) {
                const quantity = e.target.closest('.qty').querySelector('input');
                quantity.value = parseInt(quantity.value) + 1;
            }

            if (e.target.classList.contains('minus')) {
                const productCard = e.target.closest('.product-card');
                const quantity = productCard.querySelector('input[type="number"]');

                let currentQty = parseInt(quantity.value);

                if (currentQty > 0) {
                    quantity.value = currentQty - 1;

                    const variantId = productCard.querySelector('.add-btn').dataset.id;

                    const existsInBundle = this.selectedProducts.some(
                        p => String(p.id) === String(variantId)
                    );

                    if (existsInBundle) {
                        this.dispatchEvent(new CustomEvent('bundle:remove', {
                            detail: { variantId }
                        }));
                    }
                }
            }

            if (e.target.classList.contains('color-swatch') || e.target.classList.contains('size-swatch')) {
                this.onProductChange(e.target.closest('.product-card'));
                this.updateCardHighlights();
            }

            if (e.target.classList.contains('add-btn')) {
                const variantId = e.target.dataset.id;
                const variantPrice = e.target.dataset.price;
                const productCardImages = [...e.target.closest('.product-card').querySelectorAll('.popup-img')];
                const variantImage = productCardImages.find(img => img.dataset.variants.includes(variantId)) || productCardImages[0];

                const quantity = e.target.closest('.product-card').querySelector('input[type="number"]').value;

                if (variantId) {
                    this.dispatchEvent(new CustomEvent('bundle:add', {
                        detail: { variantId: variantId, count: +quantity, image: variantImage.src, price: variantPrice }
                    }));
                }
            }
        });
    }

    initializePopupCarousel(e) {
        let slides = [];
        let current = 0;

        function setupCarouselControls(e) {
            const popup = document.getElementById('global-popup');
            if (!popup || !slides.length) return;

            if (e.target.classList.contains('close-popup')) {
                popup.classList.remove('active');
            }

            if (e.target.classList.contains('next')) {
                slides[current].classList.remove('active');
                current = (current + 1) % slides.length;
                slides[current].classList.add('active');
            }

            if (e.target.classList.contains('prev')) {
                slides[current].classList.remove('active');
                current = (current - 1 + slides.length) % slides.length;
                slides[current].classList.add('active');
            }
        }

        function handlePopupImage(e) {
            const img = e.target.closest('.popup-img');
            if (!img) return;

            const popup = document.getElementById('global-popup');
            const container = popup.querySelector('.popup-images');

            const product = img.closest('.product-card');
            if (!product) return;

            const imgs = product.querySelectorAll('.popup-img');

            container.innerHTML = '';
            slides = [];
            current = 0;

            imgs.forEach((i, index) => {
                const clone = i.cloneNode(true);
                clone.classList.add('popup-slide');

                if (i === img) {
                    clone.classList.add('active');
                    current = index;
                }

                container.appendChild(clone);
                slides.push(clone);
            });

            popup.classList.add('active');
        }

        document.addEventListener('click', (e) => {
            handlePopupImage(e);
            setupCarouselControls(e);
        });
    }
}

customElements.define('bundle-builder', BundleBuilder);